import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Package, Truck, Globe, MapPin, Box, CheckCircle2, FileText, Scale } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import { Helmet } from 'react-helmet-async';

const InternationalBarrelShippingPage = () => {
  const [formData, setFormData] = useState({ 
    name: '', 
    email: '', 
    phone: '', 
    destination: '',
    numBarrels: '',
    contents: ''
  });
  const { toast } = useToast();
  
  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  
  const handleSubmit = (e) => {
    e.preventDefault();
    toast({ 
      title: "Barrel Shipping Quote Requested!", 
      description: "We'll review your barrel shipping requirements and contact you within 24 hours with pricing and availability." 
    });
    setFormData({ name: '', email: '', phone: '', destination: '', numBarrels: '', contents: '' });
  };

  const features = [
    {
      icon: Box,
      title: "Large Capacity",
      description: "Ship household goods, personal belongings, clothing, food items, and more in secure barrels."
    },
    {
      icon: Globe,
      title: "Africa & Caribbean",
      description: "Barrel shipping services available to select destinations in Africa and the Caribbean islands."
    },
    {
      icon: Scale,
      title: "Flexible Options",
      description: "Various barrel sizes available to accommodate different shipping needs and budgets."
    },
    {
      icon: FileText,
      title: "Customs Support",
      description: "Professional assistance with customs documentation and declarations for barrel shipments."
    }
  ];

  const destinations = [
    {
      region: "Caribbean Destinations",
      countries: ['Jamaica', 'Trinidad & Tobago', 'Guyana', 'Barbados', 'St. Lucia', 'Grenada']
    },
    {
      region: "African Destinations",
      countries: ['Nigeria', 'Ghana', 'Kenya', 'Contact for other countries']
    }
  ];

  const whatToShip = [
    'Household goods',
    'Clothing and textiles',
    'Non-perishable food items',
    'Personal belongings',
    'Books and educational materials',
    'Small appliances',
    'Toys and gifts',
    'Shoes and accessories'
  ];

  const faqs = [
    {
      question: "What is barrel shipping?",
      answer: "Barrel shipping is a cost-effective way to send household goods, personal belongings, clothing, and food items to family and friends in Africa and the Caribbean. Items are packed in large plastic or metal barrels for secure transport."
    },
    {
      question: "Where can barrels be shipped?",
      answer: "Shippzy offers barrel shipping to select destinations in the Caribbean (Jamaica, Trinidad, Guyana, Barbados, and other islands) and Africa. Contact us to confirm availability for your specific destination."
    },
    {
      question: "What can I send in a barrel?",
      answer: "You can send clothing, non-perishable food, household items, books, small appliances, and personal belongings. Prohibited items include hazardous materials, flammable liquids, weapons, and perishable goods. Contact us for a complete list."
    },
    {
      question: "How much does barrel shipping cost?",
      answer: "Barrel shipping costs depend on barrel size, weight, destination, and carrier. Contact us with your specific requirements for accurate pricing and delivery time estimates."
    },
    {
      question: "How long does barrel shipping take?",
      answer: "Delivery times vary by destination and shipping method. Transit typically ranges from 4-8 weeks for sea freight. Contact us for specific timeline estimates to your destination."
    }
  ];

  return (
    <>
      <Helmet>
        <title>International Barrel Shipping from Canada | Africa & Caribbean | Shippzy</title>
        <meta name="description" content="Barrel shipping from Canada to Africa and Caribbean. Send household goods, clothing, food items to Jamaica, Trinidad, Nigeria, Ghana. Professional cargo service." />
        <link rel="canonical" href="https://www.shippzy.ca/international-barrel-shipping" />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-orange-900 via-amber-900 to-yellow-800 text-white py-24 overflow-hidden">
          {/* Floating cargo boxes animation */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-10">
            {[...Array(12)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute"
                animate={{
                  y: [-100, 1000],
                  x: [Math.random() * 50 - 25, Math.random() * 50 - 25],
                  rotate: [0, 360],
                  opacity: [0, 0.3, 0]
                }}
                transition={{
                  duration: 15 + Math.random() * 10,
                  repeat: Infinity,
                  delay: Math.random() * 5
                }}
                style={{
                  left: `${Math.random() * 100}%`,
                  top: -100
                }}
              >
                <Box className="w-12 h-12 text-white" />
              </motion.div>
            ))}
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-orange-600/30 border border-orange-400/30 rounded-full"
                >
                  <Package className="w-4 h-4 text-orange-300" />
                  <span className="text-orange-200 text-sm font-semibold">Cargo & Household Goods</span>
                </motion.div>
                
                <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                  International
                  <span className="block bg-gradient-to-r from-orange-300 via-amber-300 to-yellow-300 bg-clip-text text-transparent">
                    Barrel Shipping
                  </span>
                </h1>
                
                <p className="text-xl text-gray-200 mb-8">
                  Cost-effective barrel shipping from Canada to Africa and the Caribbean. Send household goods, personal belongings, clothing, and food items to family and friends.
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    className="bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white px-8 py-6 text-lg shadow-xl font-bold"
                    onClick={() => document.getElementById('barrel-quote-form').scrollIntoView({ behavior: 'smooth' })}
                  >
                    Get Barrel Quote
                  </Button>
                  <Button 
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg"
                    onClick={() => window.location.href = '/contact'}
                  >
                    Contact Us
                  </Button>
                </div>
              </motion.div>

              {/* 3D Barrel Animation */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="hidden md:block relative"
              >
                <div className="relative">
                  {/* Glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-orange-500/30 to-transparent rounded-full blur-3xl"></div>
                  
                  {/* 3D Barrel Container */}
                  <motion.div
                    animate={{ 
                      rotateY: [0, 360],
                      y: [0, -10, 0]
                    }}
                    transition={{ 
                      rotateY: { duration: 20, repeat: Infinity, ease: "linear" },
                      y: { duration: 4, repeat: Infinity, ease: "easeInOut" }
                    }}
                    className="relative mx-auto"
                    style={{ 
                      transformStyle: 'preserve-3d',
                      width: '280px',
                      height: '350px'
                    }}
                  >
                    {/* Barrel body */}
                    <div className="absolute inset-0 bg-gradient-to-br from-orange-600 via-orange-500 to-amber-600 rounded-3xl shadow-2xl flex items-center justify-center overflow-hidden">
                      {/* Barrel ridges */}
                      <div className="absolute inset-0 flex flex-col justify-around opacity-20">
                        <div className="h-2 bg-orange-800"></div>
                        <div className="h-2 bg-orange-800"></div>
                        <div className="h-2 bg-orange-800"></div>
                        <div className="h-2 bg-orange-800"></div>
                      </div>
                      
                      {/* Shipping label */}
                      <div className="relative z-10 bg-white/90 rounded-lg p-4 shadow-lg">
                        <Package className="w-16 h-16 text-orange-600 mx-auto mb-2" />
                        <div className="text-center">
                          <div className="text-xs font-bold text-gray-800">FRAGILE</div>
                          <div className="text-xs text-gray-600">Handle With Care</div>
                        </div>
                      </div>

                      {/* Shine effect */}
                      <motion.div
                        animate={{ x: [-300, 300] }}
                        transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                        className="absolute inset-0 w-24 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12"
                      />
                    </div>

                    {/* Floating packing materials */}
                    {[...Array(6)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="absolute w-3 h-3 bg-orange-300/60 rounded-sm"
                        animate={{
                          y: [0, -60, 0],
                          x: [0, Math.random() * 30 - 15],
                          rotate: [0, 360],
                          opacity: [0.4, 0.8, 0.4]
                        }}
                        transition={{
                          duration: 3 + Math.random() * 2,
                          repeat: Infinity,
                          delay: i * 0.5
                        }}
                        style={{
                          left: `${20 + i * 12}%`,
                          bottom: 0
                        }}
                      />
                    ))}
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Why Choose Barrel Shipping?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Cost-effective solution for sending large quantities of household goods internationally
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <Card className="h-full hover:shadow-xl transition-shadow duration-300 border-t-4 border-orange-600">
                      <CardHeader>
                        <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
                          <Icon className="w-8 h-8 text-white" />
                        </div>
                        <CardTitle className="text-xl">{feature.title}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-gray-600">{feature.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Destinations Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Barrel Shipping Destinations</h2>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {destinations.map((dest, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                >
                  <Card className="border-l-4 border-orange-500">
                    <CardHeader>
                      <CardTitle className="text-2xl text-orange-600">{dest.region}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {dest.countries.map((country, i) => (
                          <li key={i} className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                            <span className="text-gray-700">{country}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* What to Ship Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">What Can You Ship in a Barrel?</h2>
            </motion.div>

            <Card className="shadow-lg">
              <CardContent className="pt-6">
                <div className="grid md:grid-cols-2 gap-4">
                  {whatToShip.map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-gray-600 mt-6 p-4 bg-orange-50 border-l-4 border-orange-500 rounded">
                  <strong>Note:</strong> Prohibited items include hazardous materials, flammable liquids, weapons, illegal substances, and perishable goods. Contact us for a complete list of restricted items.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            </motion.div>

            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg text-orange-600">{faq.question}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-700">{faq.answer}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Quote Form */}
        <section id="barrel-quote-form" className="py-20 bg-gradient-to-br from-orange-600 via-orange-700 to-amber-700">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="shadow-2xl">
              <CardHeader>
                <CardTitle className="text-3xl text-center">Get Barrel Shipping Quote</CardTitle>
                <p className="text-center text-gray-600 mt-2">
                  Request pricing and availability for your barrel shipment
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your Name"
                    className="h-12"
                  />
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Email"
                    className="h-12"
                  />
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="Phone"
                    className="h-12"
                  />
                  <Input
                    name="destination"
                    value={formData.destination}
                    onChange={handleChange}
                    required
                    placeholder="Destination (e.g., Kingston, Jamaica)"
                    className="h-12"
                  />
                  <Input
                    name="numBarrels"
                    value={formData.numBarrels}
                    onChange={handleChange}
                    required
                    placeholder="Number of Barrels"
                    className="h-12"
                  />
                  <textarea
                    name="contents"
                    value={formData.contents}
                    onChange={handleChange}
                    required
                    placeholder="Barrel contents description"
                    className="w-full h-24 px-4 py-3 border border-gray-300 rounded-md"
                  />
                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-orange-600 to-amber-600 text-white h-14 text-lg font-semibold"
                  >
                    Request Barrel Quote
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    </>
  );
};

export default InternationalBarrelShippingPage;
